// Global Instructions Rule Applied!
//
// Consolidated commitment form — one investor's commitment to a consolidated
// fund (a public.fund_group_commitments row) as a record in its own app tab
// (funds / investors amendments PR 5, 2026-10-02). It replaces the
// consolidated commitment pop-up and its separate "Allocate" mode.
//
//   /consolidated-commitments/forms/full/<fund_group_commitments.id>
//
// Sections: Commitment (amount, status, the two dates, the GP marker and the
// carry marker — both set the primary and satellite commitments; a commitment
// of 0 is the carry's own account), Allocation (the split across
// the member funds; each fund links to its own commitment) and Capital call
// notices (applies to the commitment in every member fund).
// [Delete] [Cancel | Close] [Save]; Save and Cancel only while there are
// unsaved changes. A new one is created on the New commitment form.

import React from 'react'
import { Button, Col, Row, Spin } from 'antd'

import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../../core/hooks/useRefreshOnActivate.ts'
import {
  TABLE_ACCOUNTS,
  TABLE_COMMITMENTS,
  TABLE_FUND_GROUPS,
  TABLE_INVESTORS
} from '../../../../state_manager/globals.ts'
import { commitmentTabTitle } from '../../hooks/useCommitmentForm.tsx'
import { useConsolidatedCommitmentForm } from '../../hooks/useConsolidatedCommitmentForm.tsx'
import { CommitmentAllocationSection } from '../CommitmentAllocationSection.tsx'
import { CommitmentFormHeader } from '../CommitmentFormHeader.tsx'
import { CommitmentNoticeSection, NOTICE_CONSOLIDATED_HELP } from '../CommitmentNoticeSection.tsx'

interface FullConsolidatedCommitmentFormProps {
  recordId?: string | null
}

function ConsolidatedCommitmentForm({ recordId }: { recordId: string }): React.ReactElement {
  const form = useConsolidatedCommitmentForm(recordId)
  const { openFormInTab } = useTabManagerContext()
  // a GP switch flipped on a member fund, or the investor's documents changed
  // on the Investor form, shows when the user comes back to this tab
  const attachRefresh = useRefreshOnActivate(form.refreshIfClean, { maxAgeMs: 0 })
  const { detail } = form

  if (form.loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (!detail) {
    return (
      <div className='p-10 text-center text-gray-500'>
        {form.notFound
          ? 'Consolidated commitment not found.'
          : form.loadError || 'The consolidated commitment could not be loaded.'}
        <div className='mt-3'>
          <Button onClick={form.handleClose}>Close</Button>
        </div>
      </div>
    )
  }

  const inputProps = {
    inputMode: 'edit' as const,
    labelPosition: 'above' as const,
    onChange: form.handleChange,
    validationErrors: form.validationErrors,
    validationErrorsVersion: form.validationErrorsVersion
  }

  return (
    <div ref={attachRefresh} className='h-full overflow-y-auto p-4'>
      {form.modalContextHolder}
      <CommitmentFormHeader
        title={commitmentTabTitle(detail.investors__name, detail.fund_groups__name)}
        links={[
          {
            label: 'Investor',
            text: detail.investors__name,
            onClick: () => openFormInTab(TABLE_INVESTORS, detail.investors__id, detail.investors__name)
          },
          {
            label: 'Account',
            text: detail.accounts__name,
            onClick: () => openFormInTab(TABLE_ACCOUNTS, detail.accounts__id, detail.accounts__name)
          },
          {
            label: 'Consolidated fund',
            text: detail.fund_groups__name,
            onClick: () => openFormInTab(TABLE_FUND_GROUPS, detail.fund_groups__id, detail.fund_groups__name)
          }
        ]}
        dirty={form.dirty}
        saving={form.saving}
        onSave={() => void form.handleSave()}
        onCancel={form.handleCancel}
        onClose={form.handleClose}
        onDelete={form.handleDelete}
        deleting={form.deleting}
      />

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={10}>
          <FormSection title='Commitment' titleColor='blue' scrollable={false}>
            <InputManager
              key={`commitmentFields-${form.resetVersion}`}
              {...inputProps}
              fields={[...form.commitmentFields, ...form.gpFields]}
            />
          </FormSection>
        </Col>
        <Col xs={24} xl={14}>
          <CommitmentAllocationSection
            rows={form.draft.allocations}
            core={form.draft.core}
            statusOptions={form.statusOptions}
            currencySymbol={detail.currencies__symbol}
            onRowChange={form.handleAllocationChange}
            onApplyDefaults={form.handleApplyDefaults}
            onOpenPocket={(row) =>
              openFormInTab(TABLE_COMMITMENTS, row.pocketId, commitmentTabTitle(detail.investors__name, row.fundName))
            }
          />
        </Col>
        <Col xs={24}>
          <CommitmentNoticeSection
            notice={form.draft.notice}
            investorTemplateIds={detail.prog__investor_notice_supplement_template_ids}
            options={form.noticeSupplementOptions}
            onChange={form.handleNoticeChange}
            help={NOTICE_CONSOLIDATED_HELP}
            resetVersion={form.resetVersion}
          />
        </Col>
      </Row>
    </div>
  )
}

export default function FullConsolidatedCommitmentForm({
  recordId = null
}: FullConsolidatedCommitmentFormProps): React.ReactElement {
  if (!recordId) {
    return <div className='p-10 text-center text-gray-500'>Consolidated commitment not found.</div>
  }
  return <ConsolidatedCommitmentForm recordId={recordId} />
}
